/**
 * 手機版表格卡片化（批171）：`<table v-card-table class="tbl card-table">`。
 *
 * ★ LS 後台（`admin/src/lib/cardTable.ts`）與 portal（`portal/src/lib/cardTable.ts`）各一份、內容相同
 *   （兩個前端各自建置、不共用套件）——改一邊要改另一邊；卡片的樣式在各自的 `styles/ui.css`。
 *
 * 手機版面（`@media (--mobile)`，見 `styles/ui.css` 的 `.tbl.card-table`）時表格的每一列變成一張卡，
 * 每一格前面印出它的欄名——欄名從 `<thead>` 抄到每個 `<td data-label>`，模板不必逐格手寫標籤，
 * 改表頭也不會跟卡片上的字分家。平板與桌機照樣是表格（屬性在那裡沒有作用）。
 *
 * - 有 `colspan` 的格（展開的細目列）標 `data-full`：卡片上整列寬、不印欄名。
 * - 表頭是空字串的欄（操作欄）印空標籤，CSS 讓它整列靠右。
 * - 指令在 `mounted`／`updated` 都會重抄一次：列是 `v-for` 出來的，資料一變就有新的 `<td>`。
 *
 * **橫捲提示**（批171 驗收追加）：平板上表格仍然橫捲，而操作欄釘在右側——被它蓋住的欄位只露出
 * 一兩個字，看起來像內容壞了。`v-card-table` 順便盯住外層的捲動容器（`.tbl-scroll`／`.tbl-wrap`），
 * 在容器上標 `data-scroll-more="left right"`（哪一側還有沒看到的內容），CSS 據此在釘住欄的左緣
 * 畫一道漸層。不是表格的橫捲區（例如 portal 的套裝比較表）直接掛 `v-scroll-hint`。
 */
import type { Directive } from 'vue'

/** 表格外層的捲動容器（後台 `.tbl-scroll`、portal `.tbl-wrap`）。 */
const SCROLLER = '.tbl-scroll, .tbl-wrap'
const watched = new WeakMap<HTMLElement, () => void>()

/** 依捲動位置在容器上標 `data-scroll-more`：`left`／`right`／`left right`；沒有溢出就拿掉（純函式，測試直接呼叫）。 */
export function updateScrollHint(el: HTMLElement): void {
  const max = el.scrollWidth - el.clientWidth
  const parts: string[] = []
  if (max > 1) {
    if (el.scrollLeft > 1) parts.push('left')
    if (el.scrollLeft < max - 1) parts.push('right')
  }
  if (parts.length) el.setAttribute('data-scroll-more', parts.join(' '))
  else el.removeAttribute('data-scroll-more')
}

/** 開始盯一個捲動容器（捲動、容器或內容尺寸變化時重算）；同一個容器只盯一次。 */
function watchScroller(el: HTMLElement): void {
  if (watched.has(el)) {
    updateScrollHint(el)
    return
  }
  const update = () => updateScrollHint(el)
  el.addEventListener('scroll', update, { passive: true })
  // jsdom 沒有 ResizeObserver：沒有就只靠捲動事件與 updated 重算。
  let ro: ResizeObserver | null = null
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(update)
    ro.observe(el)
    if (el.firstElementChild) ro.observe(el.firstElementChild)
  }
  watched.set(el, () => {
    el.removeEventListener('scroll', update)
    ro?.disconnect()
  })
  update()
}

function unwatchScroller(el: HTMLElement | null): void {
  if (!el) return
  watched.get(el)?.()
  watched.delete(el)
}

/** 橫捲容器的提示（不是 `.tbl` 表格的橫捲區用；表格請用 `v-card-table`，它會順便處理）。 */
export const vScrollHint: Directive<HTMLElement> = {
  mounted: watchScroller,
  updated: updateScrollHint,
  beforeUnmount: unwatchScroller,
}

/** 依表頭把每一格標上 `data-label`（純函式，測試直接呼叫）。 */
export function labelTableCells(table: HTMLTableElement): void {
  const head = table.tHead?.rows[0]
  const labels: string[] = []
  if (head) {
    for (const th of Array.from(head.cells)) {
      const text = (th.textContent ?? '').replace(/\s+/g, ' ').trim()
      for (let i = 0; i < Math.max(1, th.colSpan); i++) labels.push(text)
    }
  }
  for (const body of Array.from(table.tBodies)) {
    for (const tr of Array.from(body.rows)) {
      let col = 0
      for (const td of Array.from(tr.cells)) {
        const span = Math.max(1, td.colSpan)
        if (span > 1) {
          td.setAttribute('data-full', '')
          td.removeAttribute('data-label')
        } else {
          td.removeAttribute('data-full')
          td.setAttribute('data-label', labels[col] ?? '')
        }
        col += span
      }
    }
  }
}

function scrollerOf(table: HTMLTableElement): HTMLElement | null {
  const p = table.parentElement
  return p && p.matches(SCROLLER) ? p : null
}

export const vCardTable: Directive<HTMLTableElement> = {
  mounted(table) {
    labelTableCells(table)
    const sc = scrollerOf(table)
    if (sc) watchScroller(sc)
  },
  updated(table) {
    labelTableCells(table)
    const sc = scrollerOf(table)
    if (sc) watchScroller(sc)
  },
  // beforeUnmount：這時表格還掛在容器底下，找得到要解除的那個容器。
  beforeUnmount(table) {
    unwatchScroller(scrollerOf(table))
  },
}
